import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {FaRegUser} from 'react-icons/fa6';
import MobileMenu from './MobileMenu';
import Menu from './Menu';
import {getCookie, setCookie} from '../cookies';
import PlatformConfigurator from '../0_configuration/PlatformConfigurator';

function Header() {
    const [authorities, setAuthorities] = useState([]);
    const [username, setUsername] = useState('');
    const [signInDisplay, setSignInDisplay] = useState('block');
    const [usernameDisplay, setUsernameDisplay] = useState('none');
    const MAX_ATTEMPTS = 3;
    const delay = (ms) =>
        new Promise((resolve) => {
            setTimeout(resolve, ms);
        });

    const clearAuth = useCallback(() => {
        setCookie('access_token', '', 0);
        setCookie('refresh_token', '', 0);
        setCookie('username', '', 0);
        setCookie('color', '', 0);
        setCookie('qn_uid', '', 0);
        setUsername('');
        setAuthorities([]);
        setSignInDisplay('block');
        setUsernameDisplay('none');
    }, []);

    const fetchAccountWithRetry = useCallback(
        async (data, rememberMe, attempt = 0) => {
            const {
                access_token: accessToken,
                refresh_token: refreshToken,
                expires_in: expiresIn,
            } = data || {};
            if (!navigator.onLine) {
                await new Promise((resolve) => {
                    window.addEventListener('online', resolve, {once: true});
                });
            }
            try {
                const response = await fetch('/auth/account/me', {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json;charset=UTF-8',
                    },
                });
                if (response.status === 401 || response.status === 403)
                    throw new Error('Auth error');
                if (!response.ok) throw new Error('Server error');
                const accountData = await response.json();
                setCookie('access_token', accessToken, expiresIn);
                setCookie('username', accountData.username, expiresIn);
                const tenDays = 10 * 24 * 60 * 60;
                if (rememberMe) setCookie('refresh_token', refreshToken, tenDays);
                setUsername(accountData.username);
                setAuthorities(accountData.authorities);
                setSignInDisplay('none');
                setUsernameDisplay('flex');
                return accountData;
            } catch (err) {
                if (err.name === 'AbortError') return null;
                if (err.message === 'Auth error') {
                    clearAuth();
                    return null;
                }
                if (attempt < MAX_ATTEMPTS) {
                    await delay(2 ** attempt * 1000);
                    const r = await fetchAccountWithRetry(data, rememberMe, attempt + 1);
                    return r;
                }
                console.error(err);
            }
            return null;
        },
        [clearAuth],
    );

    const exchangeTokenWithRetry = useCallback(
        async (refreshToken, attempt = 0) => {
            if (!navigator.onLine) {
                await new Promise((resolve) => {
                    window.addEventListener('online', resolve, {once: true});
                });
            }

            try {
                const response = await fetch('/oauth/token', {
                    method: 'POST',
                    headers: {
                        Authorization: 'Basic Y2xpZW50OnNlY3JldA==',
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: `grant_type=refresh_token&refresh_token=${encodeURIComponent(
                        refreshToken,
                    )}&scope=read,write,trust`,
                });
                if (response.status === 401 || response.status === 403)
                    throw new Error('Auth error');
                if (!response.ok) throw new Error('Server error');
                const data = await response.json();
                await fetchAccountWithRetry(data, true);
            } catch (err) {
                if (err.name === 'AbortError') return null;
                if (err.message === 'Auth error') {
                    clearAuth();
                    return null;
                }
                if (attempt < MAX_ATTEMPTS) {
                    await delay(2 ** attempt * 1000);
                    const r = await exchangeTokenWithRetry(refreshToken, attempt + 1);
                    return r;
                }
                console.error(err);
            }
            return null;
        },
        [fetchAccountWithRetry, clearAuth],
    );

    const checkAccess = useCallback(() => {
        const accessToken = getCookie('access_token');
        const refreshToken = getCookie('refresh_token');
        if (!accessToken && !refreshToken) {
            clearAuth();
            return;
        }
        if (!navigator.onLine) return;
        fetch('/auth/account/me', {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json;charset=UTF-8',
            },
        })
            .then((response) => {
                if (response.status === 401 || response.status === 403) {
                    if (refreshToken) return exchangeTokenWithRetry(refreshToken);
                    throw new Error('Auth error');
                }
                if (!response.ok) throw new Error('Server error');
                return response.json();
            })
            .then((data) => {
                if (data && data.username) {
                    setCookie('username', data.username);
                    setUsername(data.username);
                    setAuthorities(data.authorities);
                    setSignInDisplay('none');
                    setUsernameDisplay('flex');
                }
            })
            .catch((err) => {
                if (err.name === 'AbortError') return;
                if (err.message === 'Auth error') clearAuth();
                console.error(err);
            });
    }, [clearAuth, exchangeTokenWithRetry]);

    useEffect(() => {
        const accessToken = getCookie('access_token');
        const refreshToken = getCookie('refresh_token');
        const cookieUsername = getCookie('username');
        if ((accessToken || refreshToken) && cookieUsername) {
            setUsername(cookieUsername);
            setSignInDisplay('none');
            setUsernameDisplay('flex');
            checkAccess();
        } else {
            setUsername(cookieUsername);
            setSignInDisplay('block');
            setUsernameDisplay('none');
        }
        const interval = setInterval(checkAccess, 5 * 60 * 1000);
        return () => {
            clearInterval(interval);
        };
    }, [checkAccess]);

    const handleClick = () => {
        clearAuth();
    };

    const admin = authorities.includes('ADMIN') || authorities.includes('MANAGER');
    const logoClass = PlatformConfigurator.getLogoClass();
    const {origin} = window.location;

    return (
        <header>
            <div className="top_header">
                <div className="logo_wrapper">
                    <Link className={`logo ${logoClass}`} to="/">
                        Home
                    </Link>
                </div>
                <Menu />
                <MobileMenu />
                <div
                    className="lang_wrapper"
                    style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                >
                    <a
                        style={{display: signInDisplay}}
                        className="my_account_link"
                        rel="noopener noreferrer"
                        href={`${origin}/personalpage/login`}
                        target="_self"
                    >
                        Sign up / Log in
                    </a>
                    <div className="username_link_wrapper" style={{display: usernameDisplay}}>
                        <span className="username_link">{username}</span>
                        <span className="username_icon">
                            <FaRegUser />
                        </span>
                        <span className="chevron" />
                        <ul>
                            <li className="username_header">
                                <span style={{color: '#d66d36'}}>{username}</span>
                            </li>
                            <li>
                                <a
                                    rel="noopener noreferrer"
                                    href={`${origin}/personalpage/homepage`}
                                    target="_self"
                                >
                                    My account
                                </a>
                            </li>
                            {admin && (
                                <li>
                                    <a
                                        rel="noopener noreferrer"
                                        href={`${origin}/controlpanel/#statistics`}
                                        target="_self"
                                    >
                                        Admin panel
                                    </a>
                                </li>
                            )}
                            <li>
                                <button
                                    type="button"
                                    className="logout_button"
                                    onClick={handleClick}
                                >
                                    Log out
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </header>
    );
}

export default Header;
